// SegmentedControl (DESIGN §3.5.3): Spur `segment-track` 40 (sm 32), aktive Fläche `segment-active`
// gleitet 200 ms. Radix RadioGroup → radiogroup/radio mit aria-checked, ←/→ wählen.
import { Fragment, useId, type ReactNode } from 'react';
import { LayoutGroup, motion } from 'motion/react';
import { RadioGroup } from 'radix-ui';
import { useControllable } from './_lib/useControllable';
import { Tooltip } from './Tooltip';
import { cn, DURATION, EASE } from '../basis/kern';

export interface SegmentItem {
  value: string;
  /** Sichtbarer Text (`text`) bzw. Name + Tooltip (`icon`, Pflicht). */
  label: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps {
  items: SegmentItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** `text` (Label sichtbar) oder `icon` (nur Icon, Label als Tooltip). */
  variant?: 'text' | 'icon';
  size?: 'md' | 'sm';
  'aria-label': string;
  className?: string;
  disabled?: boolean;
}

export function SegmentedControl({
  items,
  value: valueProp,
  defaultValue,
  onValueChange,
  variant = 'text',
  size = 'md',
  className,
  disabled,
  ...aria
}: SegmentedControlProps) {
  const id = useId();
  const [value, setValue] = useControllable(
    valueProp,
    defaultValue ?? items[0]?.value ?? '',
    onValueChange,
  );
  const iconOnly = variant === 'icon';
  return (
    <RadioGroup.Root
      value={value}
      onValueChange={setValue}
      orientation="horizontal"
      disabled={disabled}
      aria-label={aria['aria-label']}
      data-variant={variant}
      data-size={size}
      className={cn(
        'inline-flex shrink-0 items-center rounded-full bg-segment-track p-[3px]',
        size === 'md' ? 'h-10' : 'h-8',
        className,
      )}
    >
      <LayoutGroup id={id}>
        {items.map((item) => {
          const active = item.value === value;
          const segment = (
            <RadioGroup.Item
              value={item.value}
              disabled={item.disabled}
              aria-label={iconOnly ? item.label : undefined}
              className={cn(
                'relative inline-flex h-full items-center justify-center gap-2 rounded-full select-none',
                'text-fg-secondary transition-colors duration-(--bar-dur-fast) ease-standard',
                'hover:not-disabled:text-fg data-[state=checked]:text-fg',
                'disabled:cursor-not-allowed disabled:text-fg-disabled',
                iconOnly
                  ? size === 'md'
                    ? 'w-12'
                    : 'w-10'
                  : cn('px-3.5', size === 'md' ? 'text-button' : 'text-label font-medium'),
              )}
            >
              {active ? (
                <motion.span
                  layoutId="segment"
                  aria-hidden="true"
                  transition={{ duration: DURATION.base / 1000, ease: EASE }}
                  className="absolute inset-0 rounded-full bg-segment-active"
                />
              ) : null}
              {item.icon ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'relative inline-flex',
                    iconOnly && size === 'md' ? '[&>svg]:size-5' : '[&>svg]:size-4',
                  )}
                >
                  {item.icon}
                </span>
              ) : null}
              {iconOnly ? null : <span className="relative">{item.label}</span>}
            </RadioGroup.Item>
          );
          return iconOnly ? (
            <Tooltip key={item.value} content={item.label}>
              {segment}
            </Tooltip>
          ) : (
            <Fragment key={item.value}>{segment}</Fragment>
          );
        })}
      </LayoutGroup>
    </RadioGroup.Root>
  );
}
